import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { ConversionResult } from "@/lib/currency";
import { formatLocalISODate } from "@/lib/dateUtils";

interface UseCurrencyConversionOptions {
  /** The amount to convert */
  amount: number;
  /** Source currency code (e.g., 'USD') */
  fromCurrency: string;
  /** Target currency code (e.g., 'EUR') */
  toCurrency: string;
  /** Optional date for historical rate lookup */
  date?: Date;
  /** Whether to enable the query (default: true when amount > 0 and currencies differ) */
  enabled?: boolean;
  /**
   * React Query retry override. get_exchange_rate raises when no rate exists,
   * so callers that need a fast failure path (e.g. prefill flows) pass false
   * to skip the default 3-retry exponential backoff.
   */
  retry?: boolean | number;
}

/**
 * Hook to convert currency amounts using ECB exchange rates
 *
 * @example
 * ```tsx
 * const { data, isLoading, error } = useCurrencyConversion({
 *   amount: 100,
 *   fromCurrency: 'USD',
 *   toCurrency: 'EUR'
 * });
 *
 * if (data) {
 *   console.log(`${amount} USD = ${data.convertedAmount} EUR @ ${data.exchangeRate}`);
 * }
 * ```
 */
export function useCurrencyConversion({
  amount,
  fromCurrency,
  toCurrency,
  date,
  enabled,
  retry
}: UseCurrencyConversionOptions) {
  const shouldFetch = enabled ?? (amount > 0 && fromCurrency.toUpperCase() !== toCurrency.toUpperCase());

  return useQuery({
    queryKey: [
      'currency-conversion',
      amount,
      fromCurrency.toUpperCase(),
      toCurrency.toUpperCase(),
      date?.toISOString().split('T')[0]
    ],
    queryFn: async (): Promise<ConversionResult> => {
      // Same currency - no conversion needed
      if (fromCurrency.toUpperCase() === toCurrency.toUpperCase()) {
        return {
          convertedAmount: amount,
          exchangeRate: 1,
          rateDate: (date || new Date()).toISOString().split('T')[0]
        };
      }

      const { data, error } = await supabase.rpc('convert_currency', {
        p_amount: amount,
        p_from_currency: fromCurrency.toUpperCase(),
        p_to_currency: toCurrency.toUpperCase(),
        p_date: date?.toISOString().split('T')[0] || new Date().toISOString().split('T')[0]
      });

      if (error) {
        throw new Error(`Currency conversion failed: ${error.message}`);
      }

      if (!data || data.length === 0) {
        throw new Error('No conversion result returned');
      }

      return {
        convertedAmount: data[0].converted_amount,
        exchangeRate: data[0].exchange_rate,
        rateDate: data[0].rate_date
      };
    },
    enabled: shouldFetch,
    ...(retry !== undefined && { retry }),
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    gcTime: 30 * 60 * 1000, // Keep in cache for 30 minutes
  });
}

/**
 * Hook to get the exchange rate between two currencies
 */
export function useExchangeRate(
  fromCurrency: string,
  toCurrency: string,
  date?: Date,
  enabled: boolean = true
) {
  return useQuery({
    queryKey: [
      'exchange-rate',
      fromCurrency.toUpperCase(),
      toCurrency.toUpperCase(),
      date?.toISOString().split('T')[0]
    ],
    queryFn: async (): Promise<number> => {
      if (fromCurrency.toUpperCase() === toCurrency.toUpperCase()) {
        return 1;
      }

      const { data, error } = await supabase.rpc('get_exchange_rate', {
        p_from_currency: fromCurrency.toUpperCase(),
        p_to_currency: toCurrency.toUpperCase(),
        p_date: date?.toISOString().split('T')[0] || new Date().toISOString().split('T')[0]
      });

      if (error) {
        throw new Error(`Failed to get exchange rate: ${error.message}`);
      }

      return data;
    },
    enabled: enabled && fromCurrency.toUpperCase() !== toCurrency.toUpperCase(),
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });
}

/**
 * Hook to fetch all available exchange rates for a given date
 */
export function useExchangeRates(date?: Date) {
  return useQuery({
    queryKey: ['exchange-rates', date?.toISOString().split('T')[0]],
    queryFn: async () => {
      const targetDate = date?.toISOString().split('T')[0] || new Date().toISOString().split('T')[0];

      const { data, error } = await supabase
        .from('exchange_rates')
        .select('*')
        .eq('rate_date', targetDate)
        .order('target_currency');

      if (error) {
        throw new Error(`Failed to fetch exchange rates: ${error.message}`);
      }

      return data;
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });
}

/**
 * Hook to fetch the most recent exchange rates (regardless of date)
 */
export function useLatestExchangeRates() {
  return useQuery({
    queryKey: ['exchange-rates-latest'],
    queryFn: async () => {
      // Get the most recent rate date
      const { data: latestDate, error: dateError } = await supabase
        .from('exchange_rates')
        .select('rate_date')
        .order('rate_date', { ascending: false })
        .limit(1)
        .single();

      if (dateError) {
        throw new Error(`Failed to fetch latest rate date: ${dateError.message}`);
      }

      if (!latestDate) {
        return { rates: [], date: null };
      }

      // Get all rates for that date
      const { data: rates, error: ratesError } = await supabase
        .from('exchange_rates')
        .select('*')
        .eq('rate_date', latestDate.rate_date)
        .order('target_currency');

      if (ratesError) {
        throw new Error(`Failed to fetch exchange rates: ${ratesError.message}`);
      }

      return {
        rates: rates || [],
        date: latestDate.rate_date
      };
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });
}

export interface HistoricalRate {
  rate_date: string;
  rate: number;
}

/**
 * Hook to fetch historical exchange rates for a specific currency
 * Returns rates from the last N days, sorted by date ascending for charting
 */
export function useHistoricalExchangeRates(
  targetCurrency: string,
  days: number = 90,
  enabled: boolean = true
) {
  return useQuery({
    queryKey: ['exchange-rates-historical', targetCurrency.toUpperCase(), days],
    queryFn: async (): Promise<HistoricalRate[]> => {
      // Calculate cutoff date for the last N days
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - days);
      const cutoffDateStr = cutoffDate.toISOString().split('T')[0];

      const { data, error } = await supabase
        .from('exchange_rates')
        .select('rate_date, rate')
        .eq('base_currency', 'EUR')
        .eq('target_currency', targetCurrency.toUpperCase())
        .gte('rate_date', cutoffDateStr)
        .order('rate_date', { ascending: true });

      if (error) {
        throw new Error(`Failed to fetch historical rates: ${error.message}`);
      }

      return (data || []).map(d => ({
        rate_date: d.rate_date,
        rate: Number(d.rate)
      }));
    },
    enabled: enabled && !!targetCurrency,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });
}

/**
 * Historical EUR rates for SEVERAL currencies in one query — the per-row
 * sparklines on the exchange-rates card. One round-trip for the whole grid
 * instead of one `useHistoricalExchangeRates` per visible row.
 *
 * Returns rate series keyed by currency code, each sorted by date ascending.
 * The window is bounded (ECB publishes ~22 business days a month, ~30
 * currencies), so the row count stays well under PostgREST's silent cap.
 */
export function useHistoricalExchangeRateSeries(
  targetCurrencies: readonly string[],
  days: number = 30
) {
  const codes = [...new Set(targetCurrencies.map((c) => c.toUpperCase()))].sort();
  return useQuery({
    queryKey: ['exchange-rates-historical-series', codes, days],
    queryFn: async (): Promise<Record<string, HistoricalRate[]>> => {
      const cutoff = new Date();
      cutoff.setDate(cutoff.getDate() - days);

      const { data, error } = await supabase
        .from('exchange_rates')
        .select('target_currency, rate_date, rate')
        .eq('base_currency', 'EUR')
        .in('target_currency', codes)
        .gte('rate_date', formatLocalISODate(cutoff))
        .order('rate_date', { ascending: true });

      if (error) {
        throw new Error(`Failed to fetch historical rates: ${error.message}`);
      }

      const series: Record<string, HistoricalRate[]> = {};
      for (const row of data ?? []) {
        (series[row.target_currency] ??= []).push({ rate_date: row.rate_date, rate: Number(row.rate) });
      }
      return series;
    },
    enabled: codes.length > 0,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });
}
